// ============================================================================
// MediaPlayerVideo — видео-ветка ЕДИНОГО проигрывателя (SSoT): нативные
// контролы + кнопка «Картинка в картинке» (requestPictureInPicture).
// Кнопка показывается ТОЛЬКО при поддержке браузера (document
// .pictureInPictureEnabled); на мобильных/без поддержки её нет — никаких
// заглушек и «серых» кнопок.
// Пока сервер перекодирует несовместимый кодек — поверх видео «Готовим видео…»
// (controls/PiP остаются доступны: оверлей не перехватывает события).
// ============================================================================

import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Download, Loader2, PictureInPicture, PictureInPicture2 } from 'lucide-react';
import { createLogger } from '@/lib/logger';
import { MESSAGE_SINGLE_MEDIA_MAX_HEIGHT } from '@/lib/chatMessageLayout';
import type { MediaElementHandlers } from '@/components/media/mediaPlayerParts';

const log = createLogger('media');

interface Props {
  url: string;
  name?: string;
  /** Единый медиа-элемент проигрывателя (владелец — MediaPlayer). */
  mediaRef: RefObject<HTMLMediaElement | null>;
  handlers: MediaElementHandlers;
  /** Идёт подготовка перекодированного источника (ожидание сервера). */
  preparing?: boolean;
  footer?: ReactNode;
  /** Скачивание (если браузер не умеет формат — честный путь получить файл). */
  onDownload?: () => void;
}

export function MediaVideoSurface({ url, name, mediaRef, handlers, preparing, footer, onDownload }: Props) {
  const [pipSupported, setPipSupported] = useState(false);
  const [pipActive, setPipActive] = useState(false);

  // Поддержка PiP + состояние (вход/выход) — событиями самого элемента.
  useEffect(() => {
    const v = mediaRef.current as HTMLVideoElement | null;
    if (!v) { setPipSupported(false); return; }
    const supported = typeof document !== 'undefined'
      && document.pictureInPictureEnabled === true
      && typeof v.requestPictureInPicture === 'function';
    setPipSupported(supported);
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    v.addEventListener('enterpictureinpicture', onEnter);
    v.addEventListener('leavepictureinpicture', onLeave);
    return () => {
      v.removeEventListener('enterpictureinpicture', onEnter);
      v.removeEventListener('leavepictureinpicture', onLeave);
    };
  }, [mediaRef, url]);

  if (!url) {
    return <div className="flex h-40 items-center justify-center text-[11px] text-muted-foreground">Загрузка видео…</div>;
  }

  const togglePip = () => {
    const v = mediaRef.current as HTMLVideoElement | null;
    if (!v) return;
    const action = pipActive ? document.exitPictureInPicture() : v.requestPictureInPicture();
    action.catch((e: unknown) => {
      log.warn('картинка в картинке недоступна: ' + String(e));
    });
  };

  return (
    <div className="relative w-full">
      <video
        ref={mediaRef as unknown as RefObject<HTMLVideoElement>}
        src={url}
        controls
        playsInline
        preload="metadata"
        className="block h-auto w-full bg-black/30 object-contain"
        style={{ maxHeight: MESSAGE_SINGLE_MEDIA_MAX_HEIGHT }}
        title={name}
        {...handlers}
      />
      {preparing && (
        <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center">
          <div
            className="flex items-center gap-2 rounded-full bg-black/65 px-3 py-1.5 text-xs text-white shadow-sm backdrop-blur-sm"
            role="status"
            aria-live="polite"
            title="Перекодируем видео в совместимый формат"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Готовим видео…
          </div>
        </div>
      )}
      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm hover:bg-black/75"
          title="Скачать видео"
          aria-label="Скачать видео"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      )}
      {pipSupported && (
        <button
          type="button"
          onClick={togglePip}
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm hover:bg-black/75"
          title={pipActive ? 'Выйти из картинки в картинке' : 'Картинка в картинке'}
          aria-label={pipActive ? 'Выйти из картинки в картинке' : 'Картинка в картинке'}
        >
          {pipActive ? <PictureInPicture2 className="h-3.5 w-3.5" /> : <PictureInPicture className="h-3.5 w-3.5" />}
        </button>
      )}
      {footer}
    </div>
  );
}
